import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowLeft, Chrome, Users, User, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { signupSchema, type SignupData } from '@shared/schema';
import logger from '@/lib/logger';
import { trackEvent } from '@/lib/analytics/posthog';

type AccountType = 'player' | 'coach';
type Step = 'type' | 'details';

export default function SignupPage() {
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState<Step>('type');
  const [accountType, setAccountType] = useState<AccountType>('player');
  const { toast } = useToast();
  const { signup, loginWithGoogle } = useAuth();

  const form = useForm<SignupData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: SignupData) => {
    try {
      logger.info('Starting signup process...', { accountType });
      await signup(data.name, data.email, data.password, accountType);
      logger.info('Signup completed, redirecting...');
      try {
        trackEvent('signup_completed', { method: 'email', accountType });
      } catch {}
      toast({
        title: 'Account created!',
        description: accountType === 'coach'
          ? 'Welcome, Coach! Let\'s set up your team.'
          : 'Welcome to Bounce Bunny! Your account has been created successfully.',
      });
      // Add a small delay to ensure the auth state updates
      setTimeout(() => {
        if (accountType === 'coach') {
          setLocation('/create-team');
        } else {
          setLocation('/');
        }
      }, 100);
    } catch (error) {
      logger.error('Signup error:', error as any);
      try {
        trackEvent('signup_failed', {
          method: 'email',
          error_message: error instanceof Error ? error.message : 'unknown',
          account_type: accountType,
        });
      } catch {}
      toast({
        variant: 'destructive',
        title: 'Signup failed',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleGoogleSignup = async () => {
    try {
      await loginWithGoogle(accountType);
      try {
        trackEvent('signup_google_initiated', { method: 'google', accountType });
      } catch {}
      // Note: User will be redirected to Google, then back to /auth/callback
      // The callback page will handle the final redirect based on account type
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Google signup failed',
        description: error instanceof Error ? error.message : 'Unable to connect with Google. Please try again.',
      });
    }
  };

  const handleAccountTypeSelect = (type: AccountType) => {
    setAccountType(type);
    setStep('details');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-apple-blue via-apple-purple to-apple-pink relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="floating-orb bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
        <div className="floating-orb bg-gradient-to-r from-apple-purple/20 to-apple-pink/20 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
        <div className="floating-orb bg-gradient-to-r from-apple-pink/20 to-apple-orange/20 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4 sm:p-6">
        <div className="w-full max-w-md mx-auto">
          {/* Back button */}
          {step === 'type' ? (
            <Link href="/">
              <Button
                variant="ghost"
                className="mb-6 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          ) : (
            <Button
              variant="ghost"
              className="mb-6 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
              onClick={() => setStep('type')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Change Account Type
            </Button>
          )}

          {/* Step 1: Account Type Selection */}
          {step === 'type' && (
            <Card className="glass-effect border-white/20 shadow-2xl">
              <CardHeader className="text-center pb-4 sm:pb-6">
                <CardTitle className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-apple-gray bg-clip-text text-transparent">
                  Join Bounce Bunny
                </CardTitle>
                <CardDescription className="text-white/70 text-sm sm:text-base">
                  How will you be using the platform?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 px-4 sm:px-6">
                {/* Athlete Option */}
                <button
                  onClick={() => handleAccountTypeSelect('player')}
                  className="w-full p-6 rounded-2xl border-2 border-white/20 hover:border-white/50 bg-white/5 hover:bg-white/10 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] flex items-center justify-center">
                      <User className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">I'm an Athlete</h3>
                      <p className="text-sm text-white/60">Get personalized training plans and feedback on your form</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white/80 transition-colors" />
                  </div>
                </button>

                {/* Coach Option */}
                <button
                  onClick={() => handleAccountTypeSelect('coach')}
                  className="w-full p-6 rounded-2xl border-2 border-white/20 hover:border-white/50 bg-white/5 hover:bg-white/10 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--apple-orange)] to-[var(--apple-pink)] flex items-center justify-center">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">I'm a Coach</h3>
                      <p className="text-sm text-white/60">Manage your team and track athlete progress</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white/80 transition-colors" />
                  </div>
                </button>

                <div className="text-center pt-4">
                  <span className="text-white/60">Already have an account? </span>
                  <Link href="/login">
                    <Button
                      variant="link"
                      className="text-white hover:text-apple-blue p-0 h-auto"
                    >
                      Sign in
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Account Details */}
          {step === 'details' && (
            <Card className="glass-effect border-white/20 shadow-2xl">
              <CardHeader className="text-center pb-4 sm:pb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    accountType === 'coach'
                      ? 'bg-gradient-to-br from-[var(--apple-orange)] to-[var(--apple-pink)]'
                      : 'bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)]'
                  }`}>
                    {accountType === 'coach' ? (
                      <Users className="w-4 h-4 text-white" />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="text-sm text-white/60 capitalize">{accountType} Account</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-apple-gray bg-clip-text text-transparent">
                  Create Your Account
                </CardTitle>
                <CardDescription className="text-white/70 text-sm sm:text-base">
                  {accountType === 'coach'
                    ? "Set up your coach account to manage your team"
                    : "Start your journey today"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/90">Full Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Your full name"
                              {...field}
                              className="glass-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/90">Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your@email.com"
                              type="email"
                              {...field}
                              className="glass-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/90">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                placeholder="Create a password"
                                type={showPassword ? 'text' : 'password'}
                                {...field}
                                className="glass-input pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 text-white/60 hover:text-white hover:bg-transparent"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/90">Confirm Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                placeholder="Confirm your password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                {...field}
                                className="glass-input pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 text-white/60 hover:text-white hover:bg-transparent"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full apple-button"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </Form>

                <div className="relative">
                  <div className="flex items-center">
                    <div className="w-full border-t border-white/20" />
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full glass-effect border-white/20 text-white hover:bg-white/10"
                  onClick={handleGoogleSignup}
                >
                  <Chrome className="w-4 h-4 mr-2" />
                  Sign up with Google
                </Button>

                <div className="text-center">
                  <span className="text-white/60">Already have an account? </span>
                  <Link href="/login">
                    <Button
                      variant="link"
                      className="text-white hover:text-apple-blue p-0 h-auto"
                    >
                      Sign in
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
